import React from 'react';
import {twMerge} from 'tailwind-merge';

import {ColorPaletteOption} from '../../../../util/colors';
import {useStatsigGateWorkspaceConsolidation} from '../../../../util/rampFeatureFlags';
import {useSingleRunModeContext} from '../../../MultiRunWorkspace/SingleRunMode';
import {useLegendVisibility} from '../../../PanelRunsLinePlot/useLegendVisibility';
import {DrawerContentType} from '../../WorkspaceDrawerContextProvider';
import {useWorkspaceSettingsColorPalette} from '../colorPalette/ColorPalette';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {LinePlotCoordinateZooming} from './LinePlotCoordinateZooming';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {LinePlotTooltipNumRuns} from './LinePlotTooltipNumRuns';
import {useLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotDisplayPreferencesTab = ({
  displayedContentType,
}: {
  displayedContentType: DrawerContentType;
}) => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const {isSingleRun} = useSingleRunModeContext();
  const isOldSingleRunWorkspace =
    isSingleRun && !isWorkspaceConsolidationEnabled;

  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();

  const {
    colorRunNames,
    displayFullRunName,
    highlightedCompanionRunOnly,
    showLegend,
    showXRange,
  } = useLinePlotSettingsWithDefaults(displayLinePlotSettings);
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const isLegendShown = useLegendVisibility(showLegend);

  return (
    <div
      data-test="workspace-line-plot-display-preferences"
      className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
      {!isOldSingleRunWorkspace && (
        <SettingToggleRow
          clearSetting={clearSetting}
          displayValue={isLegendShown}
          emptySettings={{showLegend: undefined}}
          label="Display legends in all panels"
          numOverrides={settingOverrideCounts}
          onCheckedChange={(newValue: boolean) => {
            updateLinePlotSettings({showLegend: newValue});
            trackLinePlotSetting('showLegend', newValue.toString());
          }}
          settingKey="showLegend"
          settingLevel={settingLevel}
          value={linePlotSettings?.showLegend}
        />
      )}
      {colorPaletteOption === ColorPaletteOption.DEFAULT && (
        <SettingToggleRow
          clearSetting={clearSetting}
          displayValue={colorRunNames}
          emptySettings={{colorRunNames: undefined}}
          label="Display colored run names"
          numOverrides={settingOverrideCounts}
          onCheckedChange={(newValue: boolean) => {
            updateLinePlotSettings({colorRunNames: newValue});
            trackLinePlotSetting('colorRunNames', newValue.toString());
          }}
          settingKey="colorRunNames"
          settingLevel={settingLevel}
          value={linePlotSettings?.colorRunNames}
        />
      )}
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={showXRange}
        emptySettings={{showXRange: undefined}}
        label="Show bin range in tooltip"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({showXRange: newValue});
          trackLinePlotSetting('showXRange', newValue.toString());
        }}
        settingKey="showXRange"
        settingLevel={settingLevel}
        value={linePlotSettings?.showXRange}
      />
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={highlightedCompanionRunOnly}
        emptySettings={{highlightedCompanionRunOnly: undefined}}
        label="Only show highlighted run in companion chart tooltips"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            highlightedCompanionRunOnly: newValue,
          });
          trackLinePlotSetting(
            'highlightedCompanionRunOnly',
            newValue.toString()
          );
        }}
        settingKey="highlightedCompanionRunOnly"
        settingLevel={settingLevel}
        value={linePlotSettings?.highlightedCompanionRunOnly}
      />
      <LinePlotTooltipNumRuns />
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={displayFullRunName}
        emptySettings={{displayFullRunName: undefined}}
        label="Display full run names on the primary chart tooltip"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            displayFullRunName: newValue,
          });
          trackLinePlotSetting('displayFullRunName', newValue.toString());
        }}
        settingKey="displayFullRunName"
        settingLevel={settingLevel}
        value={linePlotSettings?.displayFullRunName}
      />
      {displayedContentType === 'workspace-settings' && (
        <LinePlotCoordinateZooming
          coordinateZooming={
            displayLinePlotSettings?.coordinateZooming ?? false
          }
          trackSetting={trackLinePlotSetting}
          updateSetting={updateLinePlotSettings}
        />
      )}
    </div>
  );
};
